import React, { useState, useMemo } from 'react';
import { Badge, Tooltip } from '@fluentui/react-components';
import {
    CheckmarkCircle20Regular,
    DismissCircle20Regular,
    Warning20Regular,
    Info16Regular,
    Search20Regular,
    ArrowSync20Regular,
} from '@fluentui/react-icons';
import { runFullAnalysis, FullAnalysisDetail, PeopleFirstDetail, evaluateMatrixQuadrant } from '../../workers/analysisHelpers';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ScoreBar({ value, max = 100 }: { value: number; max?: number }) {
    const pct = Math.round((value / max) * 100);
    const color = pct >= 70 ? 'bg-emerald-500' : pct >= 45 ? 'bg-amber-400' : 'bg-red-500';
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div className={`${color} h-2 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
            </div>
            <span className={`text-xs font-bold w-8 text-right ${pct >= 70 ? 'text-emerald-600' : pct >= 45 ? 'text-amber-500' : 'text-red-600'}`}>
                {value}
            </span>
        </div>
    );
}

function SectionCard({ title, icon, children, defaultOpen = true }: {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100 hover:bg-gray-100 transition-colors text-left"
            >
                <span className="text-[#2ba6bb]">{icon}</span>
                <h4 className="text-sm font-bold text-gray-800 flex-1">{title}</h4>
                <svg
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && <div className="px-4 py-3 space-y-2">{children}</div>}
        </div>
    );
}

function Row({ label, value, note, isCode, highlight }: {
    label: string;
    value: React.ReactNode;
    note?: string;
    isCode?: boolean;
    highlight?: 'good' | 'warn' | 'bad';
}) {
    const bg = highlight === 'good' ? 'bg-emerald-50' : highlight === 'warn' ? 'bg-amber-50' : highlight === 'bad' ? 'bg-red-50' : '';
    return (
        <div className={`flex items-start justify-between gap-4 py-1.5 px-1 rounded border-b border-gray-50 last:border-0 ${bg}`}>
            <span className="text-xs text-gray-500 shrink-0 w-36">{label}</span>
            <span className={`text-xs font-medium text-gray-900 text-right flex-1 ${isCode ? 'font-mono bg-gray-50 px-1 rounded' : ''}`}>
                {value}
            </span>
            {note && <span className="text-xs text-gray-400 shrink-0">{note}</span>}
        </div>
    );
}

type CheckStatus = 'pass' | 'warn' | 'fail';

function CheckRow({ passes, label, note, status }: {
    passes: boolean;
    label: string;
    note: string;
    status?: CheckStatus;
}) {
    const s: CheckStatus = status ?? (passes ? 'pass' : 'fail');
    const bg = s === 'pass' ? 'bg-emerald-50' : s === 'warn' ? 'bg-amber-50' : 'bg-red-50/60';
    const text = s === 'pass' ? 'text-emerald-800' : s === 'warn' ? 'text-amber-800' : 'text-red-800';
    const icon = s === 'pass'
        ? <CheckmarkCircle20Regular className="text-emerald-500 shrink-0 mt-0.5" />
        : s === 'warn'
            ? <Warning20Regular className="text-amber-500 shrink-0 mt-0.5" />
            : <DismissCircle20Regular className="text-red-500 shrink-0 mt-0.5" />;
    return (
        <div className={`flex items-start gap-2 p-2 rounded-lg ${bg}`}>
            {icon}
            <div>
                <span className={`text-xs font-semibold ${text}`}>{label}</span>
                <span className="text-xs text-gray-500 ml-1">: {note}</span>
            </div>
        </div>
    );
}

// ─── People-First Scorecard table ────────────────────────────────────────────

const PENALTY_STYLE: Record<string, string> = {
    Critical: 'bg-red-100 text-red-700',
    High: 'bg-orange-100 text-orange-700',
    Medium: 'bg-amber-100 text-amber-700',
    Low: 'bg-gray-100 text-gray-500',
};

function PeopleFirstScorecard({ pf }: { pf: PeopleFirstDetail }) {
    const rows = [
        {
            metric: 'Directives',
            threshold: '1 found',
            found: String(pf.directives.count),
            penalty: pf.directives.penalty,
            triggered: pf.directives.triggered,
            recommendation: pf.directives.recommendation,
            examples: pf.directives.examples,
        },
        {
            metric: 'Hidden Text',
            threshold: '>10 words',
            found: `${pf.hiddenText.count}\u00a0words`,
            penalty: pf.hiddenText.penalty,
            triggered: pf.hiddenText.triggered,
            recommendation: pf.hiddenText.recommendation,
            examples: [] as string[],
        },
        {
            metric: 'Unicode Junk',
            threshold: '>2 chars',
            found: String(pf.unicodeJunk.count),
            penalty: pf.unicodeJunk.penalty,
            triggered: pf.unicodeJunk.triggered,
            recommendation: pf.unicodeJunk.recommendation,
            examples: [] as string[],
        },
        {
            metric: 'Delimiters',
            threshold: '>3 pairs',
            found: String(pf.delimiters.count),
            penalty: pf.delimiters.penalty,
            triggered: pf.delimiters.triggered,
            recommendation: pf.delimiters.recommendation,
            examples: pf.delimiters.examples,
        },
        {
            metric: 'AI Attributes',
            threshold: '1 found',
            found: String(pf.aiAttributes.count),
            penalty: pf.aiAttributes.penalty,
            triggered: pf.aiAttributes.triggered,
            recommendation: pf.aiAttributes.recommendation,
            examples: pf.aiAttributes.examples,
        },
    ];

    return (
        <div className="rounded-lg border border-gray-100 overflow-x-auto text-[11px]">
            {/* Header */}
            <div className="grid grid-cols-[6rem_5rem_4rem_5rem_1fr] bg-gray-50 border-b border-gray-100 px-2 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide" style={{ minWidth: 380 }}>
                <span>Metric</span>
                <span>Threshold</span>
                <span>Found</span>
                <span>Penalty</span>
                <span>Admin Recommendation</span>
            </div>
            {rows.map((row) => (
                <div
                    key={row.metric}
                    className={`grid grid-cols-[6rem_5rem_4rem_5rem_1fr] px-2 py-2 border-b border-gray-50 last:border-0 items-start ${row.triggered ? 'bg-red-50/40' : ''}`}
                    style={{ minWidth: 380 }}
                >
                    <span className="font-semibold text-gray-700 flex items-center gap-1">
                        {row.triggered
                            ? <DismissCircle20Regular className="w-3 h-3 text-red-500 shrink-0" />
                            : <CheckmarkCircle20Regular className="w-3 h-3 text-emerald-500 shrink-0" />
                        }
                        {row.metric}
                    </span>
                    <span className="text-gray-400">{row.threshold}</span>
                    <span className={`font-bold ${row.triggered ? 'text-red-600' : 'text-emerald-600'}`}>
                        {row.found}
                    </span>
                    <span>
                        {row.triggered
                            ? <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${PENALTY_STYLE[row.penalty]}`}>{row.penalty}</span>
                            : <span className="text-gray-300">—</span>
                        }
                    </span>
                    <span className={row.triggered ? 'text-gray-700 italic' : 'text-gray-300'}>
                        {row.triggered ? `"${row.recommendation}"` : '✓ Clean'}
                        {row.triggered && row.examples.length > 0 && (
                            <Tooltip
                                content={<span className="font-mono text-[10px] whitespace-pre">{row.examples.slice(0, 3).join('\n')}</span>}
                                relationship="label"
                            >
                                <span className="ml-1 cursor-help text-[#2ba6bb] underline underline-offset-2">
                                    {row.examples.length} example{row.examples.length > 1 ? 's' : ''}
                                </span>
                            </Tooltip>
                        )}
                    </span>
                </div>
            ))}
        </div>
    );
}

/**
 * PeopleFirstRow — a collapsible CheckRow that embeds the 5-category scorecard.
 * Auto-expands when any finding is triggered so failures are immediately visible.
 */
function PeopleFirstRow({ pf }: { pf: PeopleFirstDetail }) {
    // Default open when something failed so issues are visible immediately
    const [open, setOpen] = useState(!pf.passes);
    const passes = pf.passes;

    return (
        <div className={`rounded-lg overflow-hidden border ${passes ? 'border-emerald-100' : 'border-red-200'} mb-1`}>
            {/* Header row — same visual language as CheckRow but with a toggle */}
            <button
                onClick={() => setOpen(o => !o)}
                className={`w-full flex items-center gap-2 p-2 text-left transition-colors ${passes ? 'bg-emerald-50 hover:bg-emerald-100/60' : 'bg-red-50/60 hover:bg-red-100/50'
                    }`}
            >
                {passes
                    ? <CheckmarkCircle20Regular className="text-emerald-500 shrink-0" />
                    : <DismissCircle20Regular className="text-red-500 shrink-0" />
                }
                <span className={`text-xs font-semibold flex-1 ${passes ? 'text-emerald-800' : 'text-red-800'}`}>
                    Write for People First
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-0.5 shrink-0">
                    {open ? 'Hide details' : 'View details'}
                    <svg
                        className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </span>
            </button>

            {/* Collapsible scorecard */}
            {open && (
                <div className="border-t border-gray-100">
                    <PeopleFirstScorecard pf={pf} />
                </div>
            )}
        </div>
    );
}

// ─── Weight Bar: visual breakdown of Readiness = Structure×50 + Readability×30 + 20 ──

function WeightBar({ structure, readability, readiness }: {
    structure: number;
    readability: number;
    readiness: number;
}) {
    const structurePts = Math.round(structure * 0.5);
    const readabilityPts = Math.round(readability * 0.3);
    const baselinePts = 20;

    // Widths as % of 100
    const sW = structurePts;
    const rW = readabilityPts;
    const bW = baselinePts;

    return (
        <div className="space-y-3">
            {/* Stacked bar */}
            <div className="flex h-5 rounded-full overflow-hidden gap-px">
                <Tooltip content={`Structure: ${structure} × 50% = ${structurePts} pts`} relationship="label">
                    <div className="bg-[#2ba6bb] flex items-center justify-center text-[10px] text-white font-bold cursor-help transition-all"
                        style={{ width: `${sW}%` }}>
                        {sW >= 10 ? `${sW}` : ''}
                    </div>
                </Tooltip>
                <Tooltip content={`Readability: ${readability} × 30% = ${readabilityPts} pts`} relationship="label">
                    <div className="bg-violet-500 flex items-center justify-center text-[10px] text-white font-bold cursor-help transition-all"
                        style={{ width: `${rW}%` }}>
                        {rW >= 8 ? `${rW}` : ''}
                    </div>
                </Tooltip>
                <Tooltip content={`Baseline: fixed 20 pts (every page starts here)`} relationship="label">
                    <div className="bg-gray-300 flex items-center justify-center text-[10px] text-gray-600 font-bold cursor-help transition-all"
                        style={{ width: `${bW}%` }}>
                        {bW >= 8 ? `${bW}` : ''}
                    </div>
                </Tooltip>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div className="flex flex-col gap-0.5 bg-[#e0f4f7]/50 rounded-lg p-2">
                    <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm bg-[#2ba6bb] shrink-0" />
                        <span className="font-semibold text-gray-700">Structure</span>
                    </div>
                    <span className="text-gray-500 pl-4">Score: {structure}</span>
                    <span className="text-gray-500 pl-4">Weight: ×50%</span>
                    <span className="font-bold text-[#2ba6bb] pl-4">= {structurePts} pts</span>
                </div>
                <div className="flex flex-col gap-0.5 bg-violet-50/60 rounded-lg p-2">
                    <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm bg-violet-500 shrink-0" />
                        <span className="font-semibold text-gray-700">Readability</span>
                    </div>
                    <span className="text-gray-500 pl-4">Score: {readability}</span>
                    <span className="text-gray-500 pl-4">Weight: ×30%</span>
                    <span className="font-bold text-violet-600 pl-4">= {readabilityPts} pts</span>
                </div>
                <div className="flex flex-col gap-0.5 bg-gray-50 rounded-lg p-2">
                    <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm bg-gray-300 shrink-0" />
                        <span className="font-semibold text-gray-700">Baseline</span>
                    </div>
                    <span className="text-gray-500 pl-4">Fixed</span>
                    <span className="text-gray-500 pl-4">Weight: 20%</span>
                    <span className="font-bold text-gray-600 pl-4">= {baselinePts} pts</span>
                </div>
            </div>

            {/* Formula */}
            <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center justify-between gap-2 min-w-0">
                <span className="text-[11px] font-mono text-gray-600 truncate">
                    ({structure} × 0.5) + ({readability} × 0.3) + 20
                </span>
                <span className="text-sm font-black text-gray-900 shrink-0">= {readiness}</span>
            </div>
        </div>
    );
}

// ─── Signal row for structure breakdown ──────────────────────────────────────

function SignalRow({ label, found, points, maxPoints, note }: {
    label: string;
    found: number | string;
    points: number;
    maxPoints: number;
    note?: string;
}) {
    const pct = Math.min(100, Math.round((points / maxPoints) * 100));
    const color = pct >= 70 ? 'bg-[#2ba6bb]' : pct >= 30 ? 'bg-amber-400' : 'bg-red-400';
    return (
        <div className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0">
            <span className="text-xs text-gray-500 w-24 shrink-0">{label}</span>
            <span className="text-xs font-bold text-gray-700 w-8 text-center shrink-0">{found}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className={`${color} h-1.5 rounded-full`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-semibold text-gray-700 w-16 text-right shrink-0">
                {points} / {maxPoints} pts
            </span>
            {note && <span className="text-[10px] text-gray-400 shrink-0">{note}</span>}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
    items: any[];
}

export const CopilotDebugInspector: React.FC<Props> = ({ items }) => {
    const [search, setSearch] = useState('');
    const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
    const [detail, setDetail] = useState<FullAnalysisDetail | null>(null);
    const [isComputing, setIsComputing] = useState(false);
    const [localItems, setLocalItems] = useState<any[]>(items);
    const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

    // Sync if props change
    React.useEffect(() => {
        setLocalItems(items);
    }, [items]);

    const filteredItems = useMemo(() =>
        localItems.filter(i => {
            const t = (i.Title || i.Name || '').toLowerCase();
            return !search || t.includes(search.toLowerCase());
        }).slice(0, 100),
        [localItems, search]
    );

    const selectedItem = localItems.find(i => (i.Title || i.Name) === selectedTitle);

    const compute = (item?: any) => {
        const target = item || selectedItem;
        if (!target) return;
        setIsComputing(true);
        setTimeout(() => {
            try {
                const result = runFullAnalysis(target);
                setDetail(result);
                // Sync the calculated readiness back into our local list
                // so the sidebar updates its badge immediately.
                setLocalItems(prev => prev.map(p =>
                    (p.Title || p.Name) === (target.Title || target.Name)
                        ? { ...p, Readiness: result.readiness }
                        : p
                ));
            } finally {
                setIsComputing(false);
            }
        }, 50);
    };

    const handleSelect = (item: any) => {
        setSelectedTitle(item.Title || item.Name);
        compute(item);
        setMobileView('detail');
    };

    return (
        <div className="flex min-h-[600px] sm:divide-x divide-gray-100">

            {/* ── Left: Page list ── */}
            <div className={`${mobileView === 'detail' ? 'hidden sm:flex' : 'flex'} w-full sm:w-64 shrink-0 flex-col bg-gray-50`}>
                <div className="p-3 border-b border-gray-200">
                    <div className="relative">
                        <Search20Regular className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search pages…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ba6bb]/40"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                    {filteredItems.length === 0 && (
                        <p className="p-4 text-xs text-gray-400 text-center">No pages scanned yet</p>
                    )}
                    {filteredItems.map((item, i) => {
                        const title = item.Title || item.Name || 'Untitled';
                        const readiness = Number(item.Readiness) || 0;
                        const isSelected = title === selectedTitle;
                        const dot = readiness > 80 ? 'bg-emerald-500' : readiness >= 60 ? 'bg-amber-400' : 'bg-red-500';
                        return (
                            <button
                                key={i}
                                onClick={() => handleSelect(item)}
                                className={`w-full text-left px-3 py-2.5 hover:bg-white transition-colors flex items-center gap-2 ${isSelected ? 'bg-white border-r-2 border-[#2ba6bb]' : ''}`}
                            >
                                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
                                <span className="text-xs text-gray-700 truncate flex-1">{title}</span>
                                <span className={`text-[10px] font-bold shrink-0 ${readiness > 80 ? 'text-emerald-600' : readiness >= 60 ? 'text-amber-500' : 'text-red-600'}`}>
                                    {readiness.toFixed(0)}%
                                </span>
                            </button>
                        );
                    })}
                </div>
                {items.length > 100 && (
                    <p className="px-3 py-2 text-[10px] text-gray-400 text-center border-t border-gray-200">
                        Showing first 100. Use search to narrow
                    </p>
                )}
            </div>

            {/* ── Right: Score inspector ── */}
            <div className={`${mobileView === 'list' ? 'hidden sm:flex' : 'flex'} flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-5 flex-col min-w-0`}>
                {!selectedItem ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
                        <Info16Regular className="w-10 h-10 text-gray-200" />
                        <p className="text-sm">Select a page on the left to inspect its score</p>
                        <p className="text-xs text-gray-300">Shows the exact weights and signals that produced each score</p>
                    </div>
                ) : (
                    <div className="space-y-4 w-full min-w-0">

                        {/* Mobile back button */}
                        <button
                            onClick={() => setMobileView('list')}
                            className="sm:hidden flex items-center gap-1.5 text-xs font-semibold text-[#2ba6bb] -mt-1 mb-1"
                        >
                            ← Back to list
                        </button>

                        {/* Header */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-bold text-gray-900 leading-tight break-words">
                                    {selectedItem.Title || selectedItem.Name}
                                </h3>
                                {selectedItem.webUrl && (
                                    <a href={selectedItem.webUrl} target="_blank" rel="noreferrer"
                                        className="text-xs text-[#2ba6bb] hover:underline mt-0.5 block truncate">
                                        {selectedItem.webUrl}
                                    </a>
                                )}
                            </div>
                            <button
                                onClick={() => compute()}
                                disabled={isComputing}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2ba6bb] hover:bg-[#228a9d] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 shrink-0 self-start"
                            >
                                <ArrowSync20Regular className={`w-4 h-4 ${isComputing ? 'animate-spin' : ''}`} />
                                {isComputing ? 'Computing…' : 'Re-compute'}
                            </button>
                        </div>

                        {detail && (
                            <>
                                {/* ── Section 1: Readiness Weight Breakdown ── */}
                                <SectionCard title="Readiness Score: How it was calculated" icon={<Info16Regular />}>
                                    <div className="pb-1">
                                        <div className="flex items-center gap-3 mb-3 min-w-0">
                                            <span className="text-3xl font-black text-gray-900 shrink-0">{detail.readiness}</span>
                                            <span className="text-sm text-gray-400 shrink-0">/ 100</span>
                                            <div className="flex-1 min-w-0"><ScoreBar value={detail.readiness} /></div>
                                        </div>
                                        <WeightBar
                                            structure={detail.structure.score}
                                            readability={detail.readability.score}
                                            readiness={detail.readiness}
                                        />
                                    </div>
                                </SectionCard>

                                {/* ── Section 2: Structure Drill-Down ── */}
                                <SectionCard title="Structure Score: Signal Breakdown" icon={<Info16Regular />} defaultOpen={false}>
                                    <p className="text-[11px] text-gray-400 pb-1">
                                        Positive: H1(20) + H2(12ea, max 36) + H3(5ea, max 20) + Lists(2ea, max 20) + Tables(5ea, max 10)<br />
                                        Penalties applied (limit clamps final to 20): Skipped HLevel, Empty Headers, Generic Links, Missing Table TH
                                    </p>

                                    {/* H1 */}
                                    <SignalRow
                                        label="H1 tags"
                                        found={detail.structure.h1Count}
                                        points={detail.structure.h1Score}
                                        maxPoints={20}
                                        note={detail.structure.h1Count > 0 ? '✓ Ideal' : '✗ None'}
                                    />
                                    <SignalRow
                                        label="H2 tags"
                                        found={detail.structure.h2Count}
                                        points={detail.structure.h2Score}
                                        maxPoints={36}
                                        note={`${detail.structure.h2Count} × 12pts`}
                                    />
                                    <SignalRow
                                        label="H3 tags"
                                        found={detail.structure.h3Count}
                                        points={detail.structure.h3Score}
                                        maxPoints={20}
                                        note={`${detail.structure.h3Count} × 5pts`}
                                    />
                                    <SignalRow
                                        label="List items"
                                        found={detail.structure.listItemCount}
                                        points={detail.structure.listScore}
                                        maxPoints={20}
                                        note={`${detail.structure.listItemCount} × 2pts`}
                                    />
                                    <SignalRow
                                        label="Tables"
                                        found={detail.structure.tableCount}
                                        points={detail.structure.tableScore}
                                        maxPoints={10}
                                        note={`${detail.structure.tableCount} × 5pts`}
                                    />

                                    <div className="mt-2 text-[11px] font-semibold text-gray-500 uppercase flex justify-between">
                                        <span>Positive Subtotal</span>
                                        <span className="text-[#2ba6bb]">{detail.structure.positiveTotal} / 100 max</span>
                                    </div>

                                    {detail.structure.penaltyTotal < 0 && (
                                        <div className="mt-3 bg-red-50 rounded-lg p-2 border border-red-100 divide-y divide-red-100">
                                            <div className="pb-1.5 text-[11px] font-bold text-red-700 uppercase tracking-wide">
                                                Penalties Applied ({detail.structure.penaltyTotal} pts)
                                            </div>
                                            {detail.structure.skippedLevelPenalty < 0 && (
                                                <Row label="Skipped Heading Level" value={<span className="text-red-600 font-bold">{detail.structure.skippedLevelPenalty}</span>} note="H3 used without preceding H2" highlight="bad" />
                                            )}
                                            {detail.structure.emptyHeaderPenalty < 0 && (
                                                <Row label="Empty Headers" value={<span className="text-red-600 font-bold">{detail.structure.emptyHeaderPenalty}</span>} note={`${detail.structure.emptyHeaderCount} found (cap -20)`} highlight="bad" />
                                            )}
                                            {detail.structure.genericLinkPenalty < 0 && (
                                                <Row label="Generic Links" value={<span className="text-red-600 font-bold">{detail.structure.genericLinkPenalty}</span>} note={`${detail.structure.genericLinkCount} 'click here' links (cap -15)`} highlight="bad" />
                                            )}
                                            {detail.structure.missingThTablePenalty < 0 && (
                                                <Row label="Tables missing Headers" value={<span className="text-red-600 font-bold">{detail.structure.missingThTablePenalty}</span>} note={`${detail.structure.missingThTableCount} tables missing <th> (cap -20)`} highlight="bad" />
                                            )}
                                        </div>
                                    )}

                                    {/* Total */}
                                    <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center bg-gray-50 p-2 rounded">
                                        <span className="text-xs text-gray-500">Calculated Score = max(20, Positive + Penalties)</span>
                                        <span className="text-sm font-black text-gray-700">
                                            {detail.structure.positiveTotal}{detail.structure.penaltyTotal < 0 ? ` ${detail.structure.penaltyTotal}` : ''} = <span className="text-[#2ba6bb]">{detail.structure.score}</span>
                                        </span>
                                    </div>
                                </SectionCard>

                                {/* ── Section 3: Readability Drill-Down ── */}
                                <SectionCard title="Readability Score: Flesch Reading Ease" icon={<Info16Regular />} defaultOpen={false}>
                                    <p className="text-[11px] font-mono text-gray-400 pb-1 bg-gray-50 px-2 py-1.5 rounded">
                                        206.835 − 1.015 × (words ÷ sentences) − 84.6 × (syllables ÷ words)
                                    </p>

                                    <Row label="Sentences" value={detail.readability.sentenceCount} />
                                    <Row label="Words" value={detail.readability.wordCount} />
                                    <Row label="Syllables" value={detail.readability.syllableCount} />
                                    <Row
                                        label="Words / Sentence"
                                        value={detail.readability.wordsPerSentence}
                                        note="lower = simpler"
                                        highlight={detail.readability.wordsPerSentence <= 15 ? 'good' : detail.readability.wordsPerSentence <= 25 ? 'warn' : 'bad'}
                                    />
                                    <Row
                                        label="Syllables / Word"
                                        value={detail.readability.syllablesPerWord}
                                        note="lower = simpler"
                                        highlight={detail.readability.syllablesPerWord <= 1.5 ? 'good' : detail.readability.syllablesPerWord <= 2 ? 'warn' : 'bad'}
                                    />
                                    <Row label="FRE (raw)" value={detail.readability.freRaw} />
                                    <Row label="Full calculation" value={detail.readability.formula} isCode />

                                    <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center">
                                        <span className="text-xs text-gray-500">Clamped to range 40-90</span>
                                        <span className="text-sm font-black text-violet-600">{detail.readability.score}</span>
                                    </div>
                                    <ScoreBar value={detail.readability.score} />
                                    <p className="text-[10px] text-gray-400 pt-1">Target: 60+ (plain English). 80+ = very easy. 40 = difficult academic text.</p>
                                </SectionCard>

                                {/* ── Section 4: Intent ── */}
                                <SectionCard title="Intent Classification" icon={<Info16Regular />} defaultOpen={false}>
                                    <Row
                                        label="Detected Intent"
                                        value={<Badge appearance="tint" color="brand" shape="rounded">{detail.intent.intent}</Badge>}
                                    />
                                    <Row
                                        label="Title weight (3×)"
                                        value={
                                            detail.intent.titleUsed
                                                ? <span className="text-[#2ba6bb] font-semibold">"{detail.intent.titleUsed}"</span>
                                                : <span className="text-gray-400 italic">no title, body text only</span>
                                        }
                                        note="prepended 3× before scan"
                                    />
                                    {detail.intent.matchedKeyword ? (
                                        <Row
                                            label="Triggered by"
                                            value={<span className="text-emerald-700 font-semibold">"{detail.intent.matchedKeyword}"</span>}
                                            note="first matching category wins"
                                        />
                                    ) : (
                                        <Row label="Triggered by" value={<span className="text-gray-400 italic">no keywords matched, defaulted to General</span>} />
                                    )}

                                    {/* Show all category patterns */}
                                    <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5">
                                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Category Keywords (first match wins)</p>
                                        {[
                                            { label: 'Safety', keywords: 'safety, hazard, accident, emergency, evacuation, mental health, first aid, fire, wellness, injury' },
                                            { label: 'Procedural', keywords: 'process, procedure, step, how to, guide, submitting, claiming, first step, next step, ensure, follow' },
                                            { label: 'Compliance', keywords: 'policy, code of conduct, anti-harassment, guideline, standard, mandatory, regulated, comply, approved, official' },
                                            { label: 'System', keywords: 'vpn, password, reset, expense, access, portal, login, system, tool, contact, helpdesk, support' },
                                            { label: 'Factual', keywords: 'values, who is, what is, history, leadership, executive, office, company' },
                                        ].map(({ label, keywords }) => (
                                            <div key={label} className={`text-[10px] px-2 py-1 rounded ${detail.intent.matchedRule === label ? 'bg-[#e0f4f7] text-[#186a78]' : 'text-gray-400'}`}>
                                                <strong className={detail.intent.matchedRule === label ? 'text-[#186a78]' : ''}>{label}:</strong> {keywords}
                                            </div>
                                        ))}
                                        <div className={`text-[10px] px-2 py-1 rounded ${detail.intent.intent === 'General' ? 'bg-amber-50 text-amber-700' : 'text-gray-400'}`}>
                                            <strong>General:</strong> (no keywords matched in any category above)
                                        </div>
                                    </div>
                                </SectionCard>

                                {/* ── Section 5: Readability Matrix ── */}
                                <SectionCard title="Readability Matrix Analysis" icon={<Info16Regular />} defaultOpen={true}>
                                    <p className="text-[11px] text-gray-400 pb-1">
                                        Assesses AI readiness by combining Coherence (AEW) with Readability (FRE).
                                    </p>

                                    {(() => {
                                        const aew = Number(selectedItem.AEW) || 0;
                                        if (aew === 0) {
                                            return <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-100 font-medium">Coherence (AEW) not yet calculated. Run a full site scan to index embeddings.</div>;
                                        }
                                        const matrix = evaluateMatrixQuadrant(aew, detail.readability.score, detail.intent.intent);
                                        const quadColor = matrix.quadrant === 'Best Practice' ? 'text-emerald-700 bg-emerald-50'
                                            : matrix.quadrant === 'Needs Simplification' ? 'text-blue-700 bg-blue-50'
                                                : matrix.quadrant === 'Needs Structure' ? 'text-amber-700 bg-amber-50'
                                                    : 'text-red-700 bg-red-50 border border-red-100';

                                        return (
                                            <>
                                                <Row label="Matrix Quadrant" value={<span className={`inline-block px-2 py-0.5 rounded font-bold ${quadColor}`}>{matrix.quadrant}</span>} />
                                                <Row label="Coherence (AEW)" value={aew.toFixed(4)} note={`Threshold: ≥${matrix.coherenceThresholdUsed.toFixed(2)}`} highlight={aew >= matrix.coherenceThresholdUsed ? 'good' : 'warn'} />
                                                <Row label="Readability (FRE)" value={detail.readability.score.toFixed(1)} />

                                                {matrix.readabilityOffsetApplied > 0 && (
                                                    <Row label="Intent Bonus" value={`+${matrix.readabilityOffsetApplied} FRE`} note="Applied for highly coherent Procedural guides" highlight="good" />
                                                )}

                                                <Row label="Effective FRE" value={matrix.effectiveFre.toFixed(1)} note={`Threshold: ≥${matrix.readabilityThresholdUsed}`} highlight={matrix.effectiveFre >= matrix.readabilityThresholdUsed ? 'good' : 'warn'} />
                                            </>
                                        );
                                    })()}
                                </SectionCard>

                                {/* ── Section 6: 8-Point Quality Report ── */}
                                <SectionCard title="8-Point Content Quality Report" icon={<Info16Regular />} defaultOpen={false}>
                                    <p className="text-[11px] text-gray-400 pb-1">
                                        These checks are informational. They don't directly affect the Readiness score but indicate content quality for AI readiness.
                                    </p>

                                    {/* Write for People First — collapsible 5-category scorecard */}
                                    <PeopleFirstRow pf={detail.report.peopleFirstDetail} />

                                    <CheckRow
                                        passes={!detail.report.complexLayoutPenalized}
                                        status={
                                            detail.report.complexLayoutLevel === 'Excellent' || detail.report.complexLayoutLevel === 'Standard' ? 'pass'
                                                : detail.report.complexLayoutLevel === 'Warning' ? 'warn'
                                                    : 'fail'
                                        }
                                        label="Avoid Complex Layouts"
                                        note={`Tag density: ${detail.report.metrics.tagDensity} → ${detail.report.complexLayoutLevel ?? 'Unknown'} (<0.12 Excellent · 0.12–0.18 Standard · 0.19–0.25 Warning · >0.25 Fail)`} />
                                    <CheckRow
                                        passes={detail.report.shortSections}
                                        label="Short, Purposeful Sections"
                                        note={`${detail.report.metrics.oversizeChunks} oversize paragraph(s) >450 words`} />
                                    <CheckRow
                                        passes={detail.report.clearHeadings}
                                        label="Clear Headings (H2/H3 present)"
                                        note={`${detail.report.metrics.h2h3Count} H2/H3 heading tags found`} />
                                    <CheckRow
                                        passes={detail.report.metadataPresent}
                                        label="Metadata Present"
                                        note={`Computed: "${detail.intent.intent}"${selectedItem.Intent && selectedItem.Intent !== detail.intent.intent ? ` (stored in SP: "${selectedItem.Intent}")` : ''}. Non-General means metadata is set`} />
                                    <CheckRow
                                        passes={detail.report.imagesHaveAltText}
                                        label="Images Have Alt Text"
                                        note={`${detail.report.metrics.imageCount} image(s), all must have alt ≥ 2 chars`} />
                                    <CheckRow
                                        passes={detail.report.hasStrongSummary}
                                        label="Strong Page Summary"
                                        note={`First paragraph word count: ${detail.report.metrics.firstParaWordCount} (target: 10-150)`} />
                                    <CheckRow
                                        passes={detail.report.noKeywordStuffing}
                                        label="No Keyword Stuffing"
                                        note={detail.report.metrics.ttr < 1
                                            ? `TTR: ${detail.report.metrics.ttr} ${detail.report.metrics.ttr < 0.45 ? '⚠ <0.45' : '✓'}  ·  Top phrase: "${detail.report.metrics.topNgram || detail.report.metrics.topKeyword}" (${detail.report.metrics.topNgramFrequency}%)${detail.report.metrics.stuffingSignals.length > 0 ? '  →  ' + detail.report.metrics.stuffingSignals.join(' + ') : '  →  Clean'}`
                                            : 'Not enough words to evaluate (<30 content words)'} />

                                    {/* Summary badge row */}
                                    <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                                        <span className="text-xs text-gray-500">Checks passed</span>
                                        <span className="text-sm font-black text-gray-900">
                                            {[
                                                detail.report.writeForPeopleFirst,
                                                !detail.report.complexLayoutPenalized,
                                                detail.report.shortSections,
                                                detail.report.clearHeadings,
                                                detail.report.metadataPresent,
                                                detail.report.imagesHaveAltText,
                                                detail.report.hasStrongSummary,
                                                detail.report.noKeywordStuffing,
                                            ].filter(Boolean).length} / 8
                                        </span>
                                    </div>
                                </SectionCard>

                                {/* ── Section 6: Raw Input ── */}
                                <SectionCard title="Raw Input Data" icon={<Info16Regular />} defaultOpen={false}>
                                    <Row label="Text length" value={`${(selectedItem.Text || '').length.toLocaleString()} chars`} />
                                    <Row label="HTML length" value={`${(selectedItem.HtmlContent || '').length.toLocaleString()} chars`} />
                                    <Row label="Content hash" value={selectedItem.ContentHash || '—'} isCode />
                                    <Row label="Last modified" value={selectedItem.Modified || '—'} />
                                    <Row label="Last scanned" value={selectedItem.LastScanned || '—'} />
                                </SectionCard>
                            </>
                        )}

                        {!detail && !isComputing && (
                            <p className="text-xs text-gray-400 text-center py-8">
                                Click <strong>Re-compute</strong> above to run score analysis in your browser
                            </p>
                        )}
                        {isComputing && (
                            <div className="flex items-center justify-center py-12">
                                <ArrowSync20Regular className="animate-spin w-6 h-6 text-[#2ba6bb]" />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
